import { useEffect, useState } from 'preact/hooks';
import type { PaymentOption, PublicSession } from '../api/types';
import { t } from '../i18n';
import { useI18n } from '../i18n/use-i18n';
import { mmss } from '../utils/format';

/**
 * Two deadlines matter: the per-option rate lock (amount may be re-quoted
 * after it) and the session expiry. We show the nearer one.
 */
export function Countdown({ option, session }: { option: PaymentOption; session: PublicSession }) {
  const locale = useI18n();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const rateEnd = Date.parse(option.rate_locked_until);
  const sessEnd = Date.parse(session.expires_at);
  const rateLeft = rateEnd - now;
  const sessLeft = sessEnd - now;

  if (Number.isFinite(rateLeft) && rateLeft <= 0 && sessLeft > 0) {
    return (
      <p class="cv-hint warn cv-pulse" role="status">
        {t('pay.rateExpired')}
      </p>
    );
  }

  const useRate = Number.isFinite(rateLeft) && rateLeft < sessLeft;
  const left = useRate ? rateLeft : sessLeft;
  const label = useRate ? t('pay.rateLock') : t('pay.sessionExpiresIn');
  const total = useRate ? Math.max(rateLeft, 15 * 60_000) : Math.max(sessLeft, 60 * 60_000);
  const { m, s } = mmss(left, locale);
  const low = left < 2 * 60_000;

  return (
    <div>
      <div class="cv-countdown">
        <span>{label}</span>
        <span class={`time mono${low ? ' low' : ''}`} role="timer" aria-live={low ? 'polite' : 'off'}>
          {t('time.minutesSeconds', { m, s })}
        </span>
      </div>
      <div class="cv-progress" aria-hidden="true">
        <div style={{ width: `${Math.max(0, Math.min(100, (left / total) * 100))}%` }} />
      </div>
    </div>
  );
}
